// Pure view-model helpers for the HISTORY screen of the redesigned alarms page
// (`.context/alarms-redesign/spec-history.md`). Everything here is a DECISION —
// what a row is allowed to CLAIM about an event — kept out of the component so
// it can be tested: `cloud/web`'s runner has no DOM, and a rule that can only be
// reached by mounting a component is a rule that never gets a test.
//
// The history read (§5.7) returns state TRANSITIONS and nothing else. Every
// richer cell the design draws is either a client-side join or a client-side
// pairing, and the honesty rules for both live here rather than in the render:
//
//   - `entry.actor` is ALWAYS `boat:<id>`. The human who acknowledged rides in
//     `payload.runtime.acknowledgeSource`, and is honest ONLY on a row whose own
//     transition is an Acknowledged / Silenced (`isAckOrSilenceState`) — the
//     boat never clears that field, so a later automatic row still carries the
//     stale human. `historyActorLabel` is used verbatim, gate and all.
//   - Severity and title are joined from the alerts snapshot by `alertId`. A
//     RETIRED definition has no row there: its history is real and stays, with a
//     neutral marker and an honest "A removed alarm" name.
//   - An episode's DURATION and whether anyone acknowledged it exist nowhere on
//     the wire. Both are paired client-side, forward in time from the firing
//     row — which is always complete, because pages extend BACKWARD from now, so
//     every row newer than a fetched firing is already in hand. A row whose own
//     partner is older than the fetched window (a clear whose firing we never
//     saw) claims nothing: the landed transition alone.
//   - The design's per-episode evidence ("pump running the whole time", "low
//     11.3 V · falling") has NO BACKING DATA — nothing records a min, a max, a
//     trend or a runtime across an episode. The one honest number is the value
//     at the transition, so the cell says "was 11.3 V" and stops there.
//   - There is no mate, and no undo endpoint. Neither is rendered.
//   - Nothing on this screen prints the wire's own machine text. The boat's
//     `whatHappened` is usually the evaluator's debug line, not prose, and a
//     duration is a fact about an episode rather than a millisecond reading —
//     see `historyDetail` and `formatEpisodeDuration` for both refusals.

import { describeRuntimeDisabledReason, type UnitSettings } from '@neomaris/protocol';

import {
  formatTransition,
  historyActorLabel,
  severityBadgeTone,
  type AlarmHistoryEntry,
  type AlarmSeverity,
} from './alarmDisplay';
import { formatThreshold } from './alarmSentence';
import { categoryFor } from './displayUnits';

/** The window the screen reads, and the range chip's claim: the last 30 days. */
export const HISTORY_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

/** Rows per page. Matches the disclosure this screen replaces. */
export const HISTORY_PAGE_SIZE = 50;

/** What a row says when its definition has been deleted since the event. */
export const RETIRED_ALARM_TITLE = 'A removed alarm';

/** Nothing came back at all. */
export const HISTORY_EMPTY = 'No activity recorded yet.';

/** Events exist, but the chosen chip hides them all — a different fact. */
export const HISTORY_FILTER_EMPTY = 'No events match this filter.';

/** A "load older" page failed. The rows already shown stay; the button retries. */
export const HISTORY_PAGE_ERROR = 'Couldn’t load older events — try again.';

/** The export could not fetch the whole window, so it wrote nothing at all. */
export const HISTORY_EXPORT_FAILED =
  'Couldn’t fetch the whole 30 days, so nothing was exported — try again.';

/** Export page size: the server's own per-request ceiling. */
export const HISTORY_EXPORT_PAGE_SIZE = 200;

/** Export page cap — 10,000 events; past that the export fails closed. */
export const HISTORY_EXPORT_MAX_PAGES = 50;

// ---------------------------------------------------------------------------
// Filter chips + the severity marker.
// ---------------------------------------------------------------------------

/**
 * The chips above the day groups. The design's fourth chip, `Mate actions`, has
 * no backing data (history holds state transitions only; a mate CREATE never
 * lands in `alert_state_history` at all) and is not rendered.
 */
export type HistoryFilter = 'all' | 'emergency' | 'warning';

/** The marker dot's tone — the row's only colour-borne fact. */
export type HistoryMarker = 'alarm' | 'warning' | 'neutral';

/**
 * Bucket a joined severity for the dot and the chips. Deliberately the SAME
 * buckets the nav badge uses (`severityBadgeTone`): emergency/alarm are one
 * urgency, warning another, and everything else — caution, or a retired
 * definition we cannot join at all — is neutral rather than guessed upward.
 */
export function historyMarker(severity: AlarmSeverity | null | undefined): HistoryMarker {
  const tone = severityBadgeTone(severity);
  if (tone === 'danger') return 'alarm';
  if (tone === 'attention') return 'warning';
  return 'neutral';
}

// ---------------------------------------------------------------------------
// Paging — the same explicit rules the disclosure used.
// ---------------------------------------------------------------------------

/** A stable-ish identity for a row, used only to de-dupe the paging boundary. */
export function historyRowKey(entry: AlarmHistoryEntry): string {
  return `${entry.time}|${entry.alertId}|${entry.transition}`;
}

/** What a merged page leaves the screen holding. */
export interface HistoryPageMerge {
  readonly entries: AlarmHistoryEntry[];
  /** Whether a further "Load older" can still be offered. */
  readonly hasMore: boolean;
  /** How many rows this page actually added past the boundary. */
  readonly added: number;
}

/**
 * Merge an older page into the rows already shown.
 *
 * The next page is fetched with the oldest shown row's time as an INCLUSIVE
 * `to`, so the boundary row comes back again and is dropped here. More pages are
 * only offered when this one was full AND advanced the window — a page that came
 * back full of rows we already had is the end of the list, not a loop.
 */
export function mergeHistoryPage(
  shown: readonly AlarmHistoryEntry[],
  page: readonly AlarmHistoryEntry[],
  pageSize: number
): HistoryPageMerge {
  const seen = new Set(shown.map(historyRowKey));
  const entries = [...shown];
  let added = 0;
  for (const entry of page) {
    const key = historyRowKey(entry);
    if (seen.has(key)) continue;
    seen.add(key);
    entries.push(entry);
    added += 1;
  }
  return { entries, hasMore: added > 0 && page.length >= pageSize, added };
}

/**
 * Fetch the COMPLETE bounded window, for the export — the interactive pager
 * stops wherever the reader stopped, but a file someone forwards to a surveyor
 * claims the whole 30 days and has to have walked them.
 *
 * `fetchPage(to)` is the caller's own request with its `from` fixed once, so
 * the window cannot slide between pages. Pages merge through the same
 * boundary-de-dup rules the screen pager uses. Past `maxPages` this THROWS
 * rather than returning what it has: a truncated file that says nothing about
 * the truncation is the exact lie the export exists to avoid.
 */
export async function fetchAllHistory(
  fetchPage: (to?: string) => Promise<AlarmHistoryEntry[]>,
  options: { readonly pageSize: number; readonly maxPages?: number }
): Promise<AlarmHistoryEntry[]> {
  const maxPages = options.maxPages ?? HISTORY_EXPORT_MAX_PAGES;
  let entries: AlarmHistoryEntry[] = [];
  let to: string | undefined = undefined;
  for (let page = 0; page < maxPages; page += 1) {
    const fetched = await fetchPage(to);
    const merged = mergeHistoryPage(entries, fetched, options.pageSize);
    entries = merged.entries;
    if (!merged.hasMore) return entries;
    const oldest = entries[entries.length - 1];
    if (oldest === undefined) return entries;
    to = oldest.time;
  }
  throw new Error(HISTORY_EXPORT_FAILED);
}

// ---------------------------------------------------------------------------
// Times.
// ---------------------------------------------------------------------------

/**
 * The row's time cell — wall clock only (`12:31`), because the day heading above
 * it already carries the date. An unparseable stamp falls back to the raw string
 * so a row is never blank.
 */
export function formatHistoryClock(iso: string): string {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return iso;
  return new Date(ms).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/**
 * How long something has been going ON — the outcome cell of a row that is
 * still happening: "45 s", "14 min", "3 h 20 m", "3 d".
 *
 * An elapsed span is a LIVE figure that moves while it is read, so the sub-hour
 * tiers stay coarser than a closed episode's: a still-running alarm never shows
 * a seconds remainder, because that digit would tick under the reader and add
 * nothing to "the bilge pump has been going about a quarter of an hour". Past
 * the hour there is nothing left to be coarse about, so the tail is
 * {@link formatEpisodeDuration}'s and the two halves of one column agree —
 * "still active · 3 h 20 m" reads beside "cleared · 2 h 05 m".
 */
export function formatSpanCoarse(ms: number): string {
  const span = Number.isFinite(ms) && ms > 0 ? ms : 0;
  const seconds = Math.floor(span / 1000);
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  return formatEpisodeDuration(span);
}

/**
 * How long a CLOSED episode ran — "9 s", "6 min 21 s", "14 min", "2 h 05 m",
 * "3 d".
 *
 * `alarmSentence`'s `formatDuration` is the wrong tool here and was the bug:
 * it exists to print a CONFIGURED delay exactly ("waits 5 s"), so it keeps
 * fractional seconds, and a measured span is a subtraction of two wire stamps.
 * That is how "cleared · 9.296 s" and "cleared · 6 min 21.586 s" reached the
 * page — three decimals of clock jitter dressed up as measurement. Nobody
 * asking "how long was the bilge full?" is owed a millisecond, and printing one
 * claims a precision the pairing does not have.
 *
 * So: whole seconds, always FLOORED (a span never rounds UP into a minute it
 * did not reach), and the remainder only while it is still worth reading —
 * under ten minutes. Past a day the answer is days; an owner scrolling a month
 * of history wants "3 d", not seventy-something hours.
 */
export function formatEpisodeDuration(ms: number): string {
  const span = Number.isFinite(ms) && ms > 0 ? ms : 0;
  const seconds = Math.floor(span / 1000);
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    const rest = seconds - minutes * 60;
    return minutes < 10 && rest > 0 ? `${minutes} min ${rest} s` : `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    // Padded, so a column of hour-long episodes stays readable: "2 h 05 m".
    return `${hours} h ${String(minutes - hours * 60).padStart(2, '0')} m`;
  }
  return `${Math.floor(hours / 24)} d`;
}

// ---------------------------------------------------------------------------
// Row derivation.
// ---------------------------------------------------------------------------

/** The lifecycle states that mean the alarm is STILL going on right now. */
const ONGOING_STATES = new Set(['Active', 'Silenced', 'Acknowledged']);

/**
 * The transitions that end an episode's CONDITION.
 *
 * `AwaitingAcknowledge` belongs here beside `Normal`: it is the state whose own
 * label reads "Back to normal — tap to acknowledge", so the water was back down
 * when it landed. Measuring an episode to the later `Normal` would bill the wait
 * for a human tap as time the bilge was full — a fabricated duration, and the
 * exact kind this module exists to refuse.
 */
const CLEARING_TRANSITIONS = new Set(['Normal', 'AwaitingAcknowledge']);

/** The outcome cell's ink — the only place a row's colour makes a claim. */
export type HistoryOutcomeTone = 'ongoing' | 'settled';

/** The outcome cell, or `null` when nothing can honestly be said. */
export interface HistoryOutcome {
  readonly text: string;
  readonly tone: HistoryOutcomeTone;
}

/** One fully-derived event row. */
export interface HistoryRow {
  /** React key — the row identity plus its index, since transitions repeat. */
  readonly key: string;
  readonly alertId: string;
  /** The raw ISO stamp, kept for grouping, CSV and sorting. */
  readonly iso: string;
  /** Epoch ms, or `null` when the stamp did not parse. */
  readonly atMs: number | null;
  /** `12:31`. */
  readonly clock: string;
  /** The landed transition, verbatim off the wire (`Active`, `Normal`, …). */
  readonly transition: string;
  readonly marker: HistoryMarker;
  readonly title: string;
  /** False when the definition is retired and the title is the fallback. */
  readonly titleKnown: boolean;
  /** Plain words: the boat's own prose, else the humanized transition. */
  readonly detail: string;
  readonly outcome: HistoryOutcome | null;
  /** "was 11.3 V" — the single value recorded at the transition. */
  readonly evidence: string | null;
  /** "ack 12:46 · cloud (mike)" / "not acknowledged" / "auto-cleared". */
  readonly acknowledgement: string | null;
  /** An emergency that is still going on — the one row the design tints. */
  readonly tinted: boolean;
}

/** The joins a row needs, all owned by the page. */
export interface HistoryJoins {
  /** alertId → title. Missing ⇒ the definition was deleted. */
  readonly titleById: ReadonlyMap<string, string>;
  /** alertId → severity, for the marker. Missing ⇒ neutral. */
  readonly severityById: ReadonlyMap<string, AlarmSeverity>;
  /** alertId → the alarm's CURRENT runtime state, from the alerts snapshot. */
  readonly stateById: ReadonlyMap<string, string>;
  /** alertId → the store unit of the alarm's own signal (ADR 0170 domain). */
  readonly unitById: ReadonlyMap<string, string>;
  /**
   * alertId → the alarm's RESOLVED telemetry path (`resolvedPathFor`). Only the
   * path says what a reading MEASURES, and only that decides whether the account
   * may restate it (ADR 0189) — the unit alone cannot, because engine oil
   * pressure and a barometer both declare hPa and only one of them converts.
   * Missing ⇒ the evidence cell keeps the store unit, which is what it has
   * always shown.
   */
  readonly pathById: ReadonlyMap<string, string>;
  /** The account's units (`useUnits()`). Omitted ⇒ every cell reads store units. */
  readonly units?: UnitSettings;
  /** The page clock, so "still active · 14 min" ages with everything else. */
  readonly now: number;
}

/** Where an episode that started at a firing row ended, as far as we can see. */
type EpisodeEnd =
  | { readonly kind: 'cleared'; readonly atMs: number | null }
  | { readonly kind: 'disabled' }
  | null;

interface EpisodeScan {
  readonly end: EpisodeEnd;
  /** The row that carries the human attribution, if one landed in the episode. */
  readonly ack: AlarmHistoryEntry | null;
}

/**
 * Walk FORWARD in time from a firing row (the list is newest-first, so that is
 * backwards through the array) to find how its episode ended and whether anyone
 * answered it.
 *
 * An `Acknowledged` outranks an earlier `Silenced`: muting an alarm and taking
 * responsibility for it are different acts, and the stronger one is what the
 * episode should be remembered by.
 */
function scanEpisode(
  entries: readonly AlarmHistoryEntry[],
  times: readonly number[],
  from: number
): EpisodeScan {
  const alertId = entries[from]?.alertId;
  let silenced: AlarmHistoryEntry | null = null;
  for (let j = from - 1; j >= 0; j -= 1) {
    const entry = entries[j];
    if (entry === undefined || entry.alertId !== alertId) continue;
    // A second firing with no clear between: whatever became of ours, this list
    // does not show it. Claim nothing.
    if (entry.transition === 'Active') return { end: null, ack: silenced };
    if (entry.transition === 'Acknowledged') return { end: endFrom(entries, times, j), ack: entry };
    if (entry.transition === 'Silenced') {
      silenced ??= entry;
      continue;
    }
    if (entry.transition === 'Disabled') return { end: { kind: 'disabled' }, ack: silenced };
    if (CLEARING_TRANSITIONS.has(entry.transition)) {
      return { end: { kind: 'cleared', atMs: finiteAt(times, j) }, ack: silenced };
    }
  }
  return { end: null, ack: silenced };
}

/** One parsed stamp, or `null` when it did not parse — no episode can be timed
 * against a stamp the page could not read. */
function finiteAt(times: readonly number[], index: number): number | null {
  const at = times[index];
  return at !== undefined && Number.isFinite(at) ? at : null;
}

/** Continue an interrupted scan past the acknowledgement row that stopped it. */
function endFrom(
  entries: readonly AlarmHistoryEntry[],
  times: readonly number[],
  from: number
): EpisodeEnd {
  const alertId = entries[from]?.alertId;
  for (let j = from - 1; j >= 0; j -= 1) {
    const entry = entries[j];
    if (entry === undefined || entry.alertId !== alertId) continue;
    if (entry.transition === 'Active') return null;
    if (entry.transition === 'Disabled') return { kind: 'disabled' };
    if (CLEARING_TRANSITIONS.has(entry.transition)) {
      return { kind: 'cleared', atMs: finiteAt(times, j) };
    }
  }
  return null;
}

/**
 * The evaluator's own debug line, as it arrives on `whatHappened`:
 * `AwaitingAcknowledge→Normal via ACK value=26.56999969482422`, or with a whole
 * position object where the number goes (`describeTransition` / `formatValue`
 * in `core/src/monitoring/evaluator/evaluator.ts`). It is a trace, written for
 * a log reader, and it is what the screen was printing verbatim.
 *
 * Matching it is deliberately shape-based rather than a token list: the arrow,
 * the `via`, and a SHOUTING event name together are the trace's signature,
 * and a new lifecycle event added to `EvaluatorEventType` is caught without this
 * file learning its name. The boat's genuine prose — "alarm watching is paused,
 * but this alarm is Active — holding the pause until it resolves" — cannot
 * collide with it, which is the whole point of testing the shape.
 */
const MACHINE_TRACE = /^\S+\s*→\s*\S+ via [A-Z][A-Z_]*(?: value=[\s\S]*)?$/;

/**
 * What a row says HAPPENED, in words.
 *
 * Three sources, in order of how much they know:
 *
 *  1. The boat's own sentence, when it really is a sentence. The evaluator
 *     writes prose for the cases it can explain (a held pause, a command for an
 *     alert it does not evaluate) and a machine trace for everything else; only
 *     the prose is fit to print, so the trace is treated as if absent.
 *  2. For a row that landed in `Disabled`, WHY it stopped being watched —
 *     through the shared `describeRuntimeDisabledReason`, the same describer the
 *     configured-alarms band uses, so "paused_by_owner" reads "paused by you" on
 *     both surfaces. Gated on the transition for the same reason
 *     `historyActorLabel` gates `acknowledgeSource`: the boat does not clear the
 *     field, so a later row still carries the stale reason.
 *  3. Otherwise the transition itself, through the shared `formatTransition` —
 *     the identical line today's activity list shows. It lower-cases the reason
 *     token (`CLEAR_DELAY_FIRED` → "clear delay fired"), so no SHOUTING wire
 *     constant reaches the page from here either.
 *
 * What is never appended, at any step: the reading. `value=…` printed the raw
 * float and, for a position alarm, a whole JSON object inside the sentence. The
 * one number a row may show belongs in the evidence cell, rounded and with its
 * unit — see {@link evidenceFor}.
 */
export function historyDetail(entry: AlarmHistoryEntry): string {
  const runtime = entry.payload?.runtime;
  // Both reads are `typeof`-guarded: this is wire JSON, and a row written by a
  // newer (or corrupt) build must degrade to the transition, never throw.
  const said = typeof runtime?.whatHappened === 'string' ? runtime.whatHappened.trim() : '';
  if (said.length > 0 && !MACHINE_TRACE.test(said)) return said;
  if (entry.transition === 'Disabled' && typeof runtime?.runtimeDisabledReason === 'string') {
    const why = describeRuntimeDisabledReason(runtime.runtimeDisabledReason);
    if (why !== undefined) return why;
  }
  return formatTransition({
    from: typeof entry.payload?.from === 'string' ? entry.payload.from : undefined,
    to: entry.transition,
    reason: typeof entry.payload?.reason === 'string' ? entry.payload.reason : undefined,
  });
}

/**
 * The one honest number an episode row carries: the value at the transition,
 * rounded and wearing the store unit of the alarm's own signal.
 *
 * Numbers only, and only finite ones. A boolean switch state would have to be
 * translated into words ("on", "tripped") that the wire never said; a structured
 * payload — a position is the common one — has no scalar in it at all, and the
 * only way to "print" it would be the JSON the boat happened to stringify. An
 * empty cell says "no reading" honestly. `formatThreshold` is the same rounding
 * every live value line uses (`signalLive`'s `formatValue` calls it), so a
 * reading reads identically here and on the alarm's own card: 26.6 V, never
 * 26.56999969482422 (TOM-860).
 *
 * And in the same UNIT as that card (ADR 0189): the category comes from the
 * alarm's own resolved path, `categoryFor(path, unit)`, which is the identical
 * resolution `rowUnits` makes for the live row. A history page reading "was
 * 2.5 m" under an account that reads depth in feet — beside a live row saying
 * "Now 8.2 ft" for the same signal — is the same one-screen-two-units failure,
 * just displaced in time. Both the screen and the CSV export come through here,
 * so they cannot say it two ways.
 *
 * A recipe alarm reaches this the same way a custom one does: history holds no
 * definition and no template, so there is no param key to key off, and the path
 * answers for both.
 */
function evidenceFor(
  entry: AlarmHistoryEntry,
  unit: string | undefined,
  path: string | undefined,
  units: UnitSettings | undefined
): string | null {
  const raw = entry.payload?.runtime?.currentValue;
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return null;
  const category = categoryFor(path, unit);
  return `was ${formatThreshold(raw, unit ?? null, category, units)}`;
}

/** The acknowledgement cell's wording for a row that carries an attribution. */
function ackLabel(entry: AlarmHistoryEntry): string {
  const verb = entry.transition === 'Silenced' ? 'silenced' : 'ack';
  const clock = formatHistoryClock(entry.time);
  const who = historyActorLabel(entry);
  // `historyActorLabel` falls back to `boat` when nobody is named — an alarm
  // answered at the panel. "ack 12:46 · boat" adds nothing over "ack 12:46".
  return who === 'boat' ? `${verb} ${clock}` : `${verb} ${clock} · ${who}`;
}

/**
 * Derive every row from one fetched, newest-first list of entries.
 *
 * Rows are built from the WHOLE list before any filter is applied, because the
 * pairing that gives a row its outcome and its acknowledgement reads its
 * neighbours — filtering first would let a hidden clear turn an episode that
 * ended into one that reads as still running.
 */
export function buildHistoryRows(
  entries: readonly AlarmHistoryEntry[],
  joins: HistoryJoins
): HistoryRow[] {
  const times = entries.map((entry) => Date.parse(entry.time));
  // The newest row per alarm — the only row allowed to speak in the present
  // tense, because it is the only one the current runtime state describes.
  const newestIndex = new Map<string, number>();
  entries.forEach((entry, i) => {
    if (!newestIndex.has(entry.alertId)) newestIndex.set(entry.alertId, i);
  });

  return entries.map((entry, i) => {
    const atRaw = times[i];
    const atMs = atRaw !== undefined && Number.isFinite(atRaw) ? atRaw : null;
    const severity = joins.severityById.get(entry.alertId);
    const marker = historyMarker(severity);
    const title = joins.titleById.get(entry.alertId);
    // Three things must agree before a row speaks in the present tense: it is
    // the newest row for its alarm, the snapshot says that alarm is still going
    // on, AND the row's own transition is one of those ongoing states. The last
    // guard is the one that matters in practice — history is fetched once, the
    // snapshot refreshes every 30 s, so an alarm that fires AFTER the fetch
    // leaves the previous episode's `Normal` sitting as the newest row we hold.
    // Without the guard that row would print "still active", timed from the
    // moment the last episode ENDED.
    const ongoingNow =
      newestIndex.get(entry.alertId) === i &&
      ONGOING_STATES.has(entry.transition) &&
      ONGOING_STATES.has(joins.stateById.get(entry.alertId) ?? '');

    const episode = entry.transition === 'Active' ? scanEpisode(entries, times, i) : null;

    let outcome: HistoryOutcome | null = null;
    if (ongoingNow && atMs !== null) {
      outcome = { text: `still active · ${formatSpanCoarse(joins.now - atMs)}`, tone: 'ongoing' };
    } else if (episode?.end?.kind === 'cleared' && atMs !== null && episode.end.atMs !== null) {
      const span = episode.end.atMs - atMs;
      // A clear stamped before its firing is a clock the page cannot reconcile;
      // saying nothing beats printing a negative episode.
      if (span >= 0)
        outcome = { text: `cleared · ${formatEpisodeDuration(span)}`, tone: 'settled' };
    }

    let acknowledgement: string | null = null;
    if (episode !== null) {
      if (episode.ack !== null) acknowledgement = ackLabel(episode.ack);
      else if (episode.end?.kind === 'cleared') acknowledgement = 'auto-cleared';
      else if (episode.end === null && ongoingNow) acknowledgement = 'not acknowledged';
    }

    return {
      key: `${historyRowKey(entry)}|${i}`,
      alertId: entry.alertId,
      iso: entry.time,
      atMs,
      clock: formatHistoryClock(entry.time),
      transition: entry.transition,
      marker,
      title: title ?? RETIRED_ALARM_TITLE,
      titleKnown: title !== undefined,
      detail: historyDetail(entry),
      outcome,
      evidence: evidenceFor(
        entry,
        joins.unitById.get(entry.alertId),
        joins.pathById.get(entry.alertId),
        joins.units
      ),
      acknowledgement,
      tinted: outcome?.tone === 'ongoing' && marker === 'alarm',
    };
  });
}

/** Keep the rows one chip asks for. `all` keeps everything, retired rows too. */
export function filterHistoryRows(
  rows: readonly HistoryRow[],
  filter: HistoryFilter
): HistoryRow[] {
  if (filter === 'all') return [...rows];
  const want: HistoryMarker = filter === 'emergency' ? 'alarm' : 'warning';
  return rows.filter((row) => row.marker === want);
}

// ---------------------------------------------------------------------------
// Day groups.
// ---------------------------------------------------------------------------

/** One day's card, heading included. */
export interface HistoryDayGroup {
  /** `2026-08-14` in the reader's own zone, or `undated`. */
  readonly key: string;
  /** `TODAY · AUG 14`, `YESTERDAY · AUG 13`, `MAY 19`, `MAY 19, 2025`. */
  readonly heading: string;
  readonly rows: HistoryRow[];
}

function localDayKey(ms: number): string {
  const d = new Date(ms);
  const month = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}

function dayHeading(ms: number, now: number): string {
  const day = new Date(ms);
  const today = new Date(now);
  const sameYear = day.getFullYear() === today.getFullYear();
  const label = day
    .toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      ...(sameYear ? {} : { year: 'numeric' }),
    })
    .toLocaleUpperCase();
  const key = localDayKey(ms);
  if (key === localDayKey(now)) return `TODAY · ${label}`;
  if (key === localDayKey(now - 24 * 60 * 60 * 1000)) return `YESTERDAY · ${label}`;
  return label;
}

/**
 * Group rows into day cards, in the order they arrived (newest first).
 *
 * Days are the reader's OWN days: a boat three time zones away still files its
 * 03:12 firing under the night the reader was asleep, because the reader is who
 * the heading is for. A row whose stamp did not parse keeps its place in the
 * list under an `UNDATED` heading rather than being silently dropped.
 */
export function groupHistoryByDay(rows: readonly HistoryRow[], now: number): HistoryDayGroup[] {
  const groups: HistoryDayGroup[] = [];
  const byKey = new Map<string, HistoryDayGroup>();
  for (const row of rows) {
    const key = row.atMs === null ? 'undated' : localDayKey(row.atMs);
    let group = byKey.get(key);
    if (group === undefined) {
      group = {
        key,
        heading: row.atMs === null ? 'UNDATED' : dayHeading(row.atMs, now),
        rows: [],
      };
      byKey.set(key, group);
      groups.push(group);
    }
    group.rows.push(row);
  }
  return groups;
}

/**
 * The footer's left caption.
 *
 * It has to be careful about two numbers the page could easily conflate: the
 * rows in hand are the newest PAGES of the window, not the window — so while
 * more can still be loaded it says "the newest N", and only once paging has run
 * out does it claim N events in the 30 days, which by then it has counted. And
 * with a chip selected, the count on screen is a filtered slice of those loaded
 * rows — "3 events in the last 30 days" through an Emergencies lens would be
 * the caption adopting the filter's view as the boat's history.
 */
export function historyCountCaption(input: {
  /** Every loaded row, before the chip filter. */
  readonly loaded: number;
  /** The rows the chip actually shows. */
  readonly matching: number;
  /** Whether a chip other than "All events" is selected. */
  readonly filtered: boolean;
  readonly hasMore: boolean;
}): string {
  const events = `${input.loaded} ${input.loaded === 1 ? 'event' : 'events'}`;
  if (!input.filtered) {
    return input.hasMore
      ? `Showing the newest ${events} · last 30 days`
      : `${events} in the last 30 days`;
  }
  return input.hasMore
    ? `Showing ${input.matching} of the newest ${events} · last 30 days`
    : `${input.matching} of ${events} in the last 30 days`;
}

/**
 * The filter-empty line. While older pages remain unloaded, "no events match"
 * is a claim about the LOADED slice only — an emergency at row 51 exists, this
 * page just has not fetched it — so the line says where else to look.
 */
export function historyFilterEmptyText(hasMore: boolean): string {
  return hasMore
    ? 'No loaded events match this filter — load older to search further back.'
    : HISTORY_FILTER_EMPTY;
}

// ---------------------------------------------------------------------------
// CSV export.
// ---------------------------------------------------------------------------

const CSV_HEADER = [
  'Time',
  'Alarm',
  'Alarm id',
  'Event',
  'Detail',
  'Outcome',
  'Reading at the event',
  'Acknowledgement',
];

/**
 * Neutralize-then-quote. Two separate hazards share this function:
 *
 *   - RFC 4180 delimiters — commas, quotes, newlines. `whatHappened` is free
 *     boat prose and titles are owner-typed, so both occur.
 *   - Spreadsheet formulas. Excel, Sheets and LibreOffice EXECUTE a cell whose
 *     first meaningful character is `=`, `+`, `-` or `@` — even behind leading
 *     blanks, and even inside RFC quotes — and a leading tab/CR re-triggers
 *     that parse in some importers. Titles are owner-typed and this file is
 *     built to be forwarded, so a title of `=HYPERLINK(...)` must land as
 *     text. The standard defusal is a leading apostrophe; it costs an
 *     occasional visible `'` on a cell that genuinely starts with `-`, which
 *     is the accepted price everywhere this mitigation is used.
 */
function csvCell(value: string): string {
  // eslint-disable-next-line no-control-regex -- skipping the control range IS the mitigation
  const risky = /^[\s\x00-\x1f]*[=+\-@]/.test(value) || /^[\t\r]/.test(value);
  const neutralized = risky ? `'${value}` : value;
  return /[",\r\n]/.test(neutralized) ? `"${neutralized.replace(/"/g, '""')}"` : neutralized;
}

/**
 * Serialize the rows the reader can actually see — the same joins, the same
 * derived cells, the same order. A CSV that quietly carried facts the screen
 * withheld (or withheld facts the screen showed) would be a second, disagreeing
 * account of the same events.
 *
 * The time column is the raw ISO stamp, not the row's clock: a spreadsheet needs
 * a sortable, unambiguous instant more than it needs the day heading's context.
 */
export function historyCsv(rows: readonly HistoryRow[]): string {
  const lines = [CSV_HEADER.join(',')];
  for (const row of rows) {
    lines.push(
      [
        row.iso,
        row.title,
        row.alertId,
        row.transition,
        row.detail,
        row.outcome?.text ?? '',
        row.evidence ?? '',
        row.acknowledgement ?? '',
      ]
        .map(csvCell)
        .join(',')
    );
  }
  return `${lines.join('\r\n')}\r\n`;
}
